import yup from 'yup'
import axios from 'axios'
import uniq from 'lodash/uniq'
import compact from 'lodash/compact'

const checkEmailDomainUppercase = value => {
  if (
    yup
      .string()
      .email()
      .isValidSync(value)
  ) {
    if (!value) return true

    const domainName = value.substring(value.indexOf('@'))
    if (domainName === domainName.toLowerCase()) return true

    return false
  }

  return true
}

function getEmployeeValidationSchema (employee) {
  return yup.object().shape({
    title: yup
      .string()
      .required('Title field is required')
      .typeError('Title field is required'),
    firstName: yup.string().required('First name field is required'),
    lastName: yup.string().required('Last name field is required'),
    email: yup
      .string()
      .nullable()
      .required('Email field is required')
      .email('Email format is invalid')
      .test(
        'domainNameUppercase',
        'Domain name must be typed in lower case',
        value => {
          return checkEmailDomainUppercase(value)
        }
      )
      .test('unique', 'Email is already in use', value => {
        if (
          yup
            .string()
            .email()
            .isValidSync(value)
        ) {
          if (!value) return true
          let payload = { email: value }
          if (employee) {
            payload = { ...payload, id: employee.id }
          }
          return axios
            .post('/api/email-check', payload)
            .then(res => {
              return res.data.valid
            })
            .catch(_ => false)
        }

        return true
      }),
    address: yup.object().shape({
      phones: yup.array().of(
        yup.object().shape({
          phoneNum: yup
            .string()
            .matches(
              /^[0-9\-+/\s]*$/,
              "Phone number can contain only digits, '+', '-', ' ' and '/'"
            )
            .required('Phone field is required')
        })
      ),
      emails: yup.array().of(
        yup.object().shape({
          email: yup
            .string()
            .email('Email format is invalid')
            .test(
              'domainNameUppercase',
              'Domain name must be typed in lower case',
              value => {
                return checkEmailDomainUppercase(value)
              }
            )
            .test('unique', 'Email is already in use', value => {
              if (
                yup
                  .string()
                  .email()
                  .isValidSync(value)
              ) {
                if (!value) return true // fallback to required error
                let payload = { email: value }
                if (employee) {
                  payload = { ...payload, id: employee.id }
                }
                return axios
                  .post('/api/email-check', payload)
                  .then(res => {
                    return res.data.valid
                  })
                  .catch(_ => false)
              }

              return true
            })
        })
      )
    }),
    phoneNotRepeating: yup
      .mixed()
      .test(
        'phoneNotRepeating',
        'You are using the same phone number in multiple fields',
        function () {
          const phones = compact(
            this.parent.address.phones.map(phone => phone.phoneNum)
          )
          const uniquePhones = compact(uniq(phones))

          return phones.length === uniquePhones.length
        }
      ),
    emailNotRepeating: yup
      .mixed()
      .test(
        'emailNotRepeating',
        'You are using the same email in multiple fields',
        function () {
          const primaryEmail = this.parent.email
          const restOfEmails = this.parent.address.emails.map(
            email => email.email
          )
          const emails = [primaryEmail, ...restOfEmails]

          const uniqueEmails = uniq(emails)

          return emails.length === uniqueEmails.length
        }
      ),
    employee: yup.object().shape({
      expertize: yup.string().nullable()
    }),
    currentPosition: yup.object().shape({
      positionDescription: yup
        .string()
        .nullable()
        .required('Position description field is required')
    }),
    languages: yup.array().of(
      yup.object().shape({
        id: yup
          .number()
          .transform(emptyStringToNull)
          .nullable(),
        languageToUser: yup.object().shape({
          languageProficiencyId: yup
            .number()
            .transform(emptyStringToNull)
            .nullable()
        })
      })
    )
  })
}

export default getEmployeeValidationSchema

function emptyStringToNull (currentValue, originalValue) {
  return originalValue === '' ? null : currentValue
}
